import { DataSource } from '@angular/cdk/collections';
import { MatSort } from '@angular/material/sort';
import { debounceTime, distinctUntilChanged, map, shareReplay, startWith, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, switchMap } from 'rxjs';
import {
    BusinessVolumeCostStateEnum,
    BusinessVolumeEntity,
    BusinessVolumeStatusEnumNameMap,
    BusinessVolumeTypeEnumNameMap,
} from '../../dave-data-module/entities/business-volume.entity';
import {
    LedgerImportDocumentTypeNames,
    LedgerImportDocumentTypes,
} from '../../dave-data-module/entities/ledger-import.entity';
import { getBVCustomNameDictionary } from '../../dave-data-module/State/selectors/b-v-custom-name.selector';
import { getFetched$ } from '../../dave-data-module/helper/helper';
import {
    getCommissionDictionary,
    getCommissionsFetched,
} from '../../dave-data-module/State/selectors/commission.selector';
import {
    getChartOfAccountDictionary,
    getChartOfAccountsFetched,
} from '../../dave-data-module/State/selectors/chartOfAccount.selectors';
import {
    getLedgerImportDictionary,
    getLedgerImportsFetched,
} from '../../dave-data-module/State/selectors/ledger-import.selector';
import { formatCurrency, formatDate } from '@angular/common';
import { isNotNullOrUndefined, MathRound, SearchQueriesDebounceTime } from '../../helper/helper';
import { FilterTypes } from '../../services/default-filter.service';
import { Moment } from 'moment/moment';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from '../../dave-data-module/State';
import { CustomerDataService } from '../../dave-data-module/services/customer-data.service';
import { FileDataService } from '../../dave-data-module/services/file-data.service';
import {
    IFilterTypeSearchMultiSelectValue,
} from '../../dave-utils-module/app-filter-module/app-filter/app-filter.component';
import { MatLegacyPaginator } from '@angular/material/legacy-paginator';

export interface BusinessVolumeTableItem {
    amountNumber: number;
    amount: string;
    payedAmountNumber: number;
    payedAmount: string;
    label: string;
    id: number;
    bvEntity: BusinessVolumeEntity;
    cssClass?: string;
    businessVolumeNo: string;
    commission: string;
    name: string;
    status: string;
    bVCustomNames: string;
    description: string;
    createdAt: string;
    createdAtAsMillis: number;
    customerName: string;
    customerNr_KontoNr: string;
    type: string;
    consolidatedDate: string;
    consolidatedDateAsMillis: number;
    consolidatedDeliveryDate: string;
    consolidatedDeliveryDateAsMillis: number;
    dueDate: string;
    dueDateAsMillis: number;
    exportDate: string;
    exportDateAsMillis: number;
    exportTimespan: string;
    lastPaymentDate: string;
    lastPaymentDateAsMillis: number;
    lastPaymentNote: string;
    lastPaymentGross: string;
    lastPaymentNet: string;
    payedAmountGross: string;
    amountGross: string;
    discountValue: string;
    discountValueGross: string;
    ledgerImportDocType: string;
    ledgerImportDocTypeId: LedgerImportDocumentTypes;
    checked: boolean;
}

export interface BvFilterValues {
    [FilterTypes.Type]: BusinessVolumeCostStateEnum;
    [FilterTypes.Status]: IFilterTypeSearchMultiSelectValue[];
    [FilterTypes.CustomNames]: IFilterTypeSearchMultiSelectValue[];
    [FilterTypes.Export]: string;
    [FilterTypes.Checked]: string;
    [FilterTypes.LedgerImportDocTypes]: IFilterTypeSearchMultiSelectValue[];
    [FilterTypes.Commissions]: IFilterTypeSearchMultiSelectValue<number>[]
    [FilterTypes.Customers]: number | null
    [FilterTypes.ContraChartOfAccounts]: IFilterTypeSearchMultiSelectValue[]
    [FilterTypes.Date]: { to: Moment; from: Moment };
    [FilterTypes.ConsolidatedDate]: { to: Moment; from: Moment };
    [FilterTypes.DueDate]: { to: Moment; from: Moment };
    [FilterTypes.SubCommissions]: IFilterTypeSearchMultiSelectValue<number>[];
}

/**
 * Data source for the Abcde view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
export class BusinessVolumesDialogDatasource extends DataSource<BusinessVolumeTableItem> {
    paginator: MatLegacyPaginator | undefined;
    sort: MatSort | undefined;
    readonly searchQuery$ = new BehaviorSubject<string>('');
    filter$: Observable<BvFilterValues>;
    loading$ = new BehaviorSubject<boolean>(false);

    private store = inject(Store<State>);
    private fileDataService = inject(FileDataService);
    private customerDataService = inject(CustomerDataService);

    private customers$ = this.BVS$.pipe(
        map(bvs => [...new Set(bvs.map(b => b.CustomerId).filter(isNotNullOrUndefined))]),
        switchMap(ids => this.customerDataService.GetCustomersDict$(ids)),
        shareReplay({refCount: true, bufferSize: 1}),
    );

    private files$ = this.BVS$.pipe(
        map(bvs => [...new Set(bvs.flatMap(b => b.DocumentIds))]),
        switchMap(ids => this.fileDataService.GetFilesById(ids)),
        shareReplay({refCount: true, bufferSize: 1}),
    );

    private data$ = combineLatest([
        this.BVS$,
        this.store.select(getBVCustomNameDictionary),
        getFetched$(this.store, getCommissionsFetched, getCommissionDictionary),
        getFetched$(this.store, getChartOfAccountsFetched, getChartOfAccountDictionary),
        getFetched$(this.store, getLedgerImportsFetched, getLedgerImportDictionary),
        this.customers$,
        this.files$,
    ]).pipe(
        map(([bvs, customNames, commissions, chartDic, ledgerImportDict, customers, files]) => {
            const fileMap = new Map(files.map(file => [file.Id, file]));
            return bvs.map<BusinessVolumeTableItem>((b) => {
                const customer = customers ? customers[b.CustomerId] : null;
                const customerNr = customer ? (customer.CustomerNo !== null && customer.CustomerNo !== undefined ? customer.CustomerNo : '') : '';
                const kontoNr = customer
                    ? customer.KontoCOAId === null || chartDic[customer.KontoCOAId] === null || customer.KontoCOAId === undefined || chartDic[customer.KontoCOAId] === undefined
                        ? ''
                        : chartDic[customer.KontoCOAId]?.Number
                    : '';

                const separator = kontoNr !== '' ? ' / ' : '';
                const customerNr_KontoNr = customerNr + separator + kontoNr;
                const exportTimespan = b ? (b.ExportPeriodStart && b.ExportPeriodEnd ? formatDate(b.ExportPeriodStart, 'mediumDate', 'de-DE') + ' - ' + formatDate(b.ExportPeriodEnd, 'mediumDate', 'de-DE') : '') : '';

                const paymentInfos = b?.PaymentInformations || [];
                const lastPayment = paymentInfos.length > 0 ? paymentInfos[paymentInfos.length - 1] : null;
                const isChecked = b.DocumentIds?.length ? b.DocumentIds.every(id => fileMap.get(id)?.Checked === true) : false;

                return {
                    id: b.Id,
                    label: b.Name,
                    amount: b.Amount ? formatCurrency(b.Amount / 100, 'de-DE', '€', 'EUR') : '',
                    amountNumber: b.Amount,
                    payedAmount: b.PayedAmount ? formatCurrency(b.PayedAmount / 100, 'de-DE', '€', 'EUR') : '',
                    payedAmountNumber: b.PayedAmount,
                    bvEntity: b,
                    businessVolumeNo: b.BusinessVolumeNo,
                    name: b.Name,
                    type: (!b.BVType || b.BVType === BusinessVolumeCostStateEnum.Notset) ? '' : BusinessVolumeTypeEnumNameMap.get(b.BVType),
                    checked: isChecked,
                    status: b.Status ? BusinessVolumeStatusEnumNameMap.get(b.Status) : undefined,
                    bVCustomNames: b.BVCustomNamesIds.map((id) => customNames[id]?.Name).join(', '),
                    description: b.Description,
                    createdAt: formatDate(b.CreatedAt, 'dd.MM.yyyy', 'de-DE'),
                    createdAtAsMillis: b.CreatedAt.getTime(),
                    customerName: customer ? customer.Name : '',
                    customerNr_KontoNr: customerNr_KontoNr,
                    commission: b.CommissionId ? commissions[b.CommissionId]?.DisplayName : '',
                    commissionLabel: b.CommissionId ? commissions[b.CommissionId]?.GetDisplayName() : '',
                    consolidatedDate: b ? (b.ConsolidatedDate ? formatDate(b.ConsolidatedDate, 'mediumDate', 'de-DE') : '') : '',
                    consolidatedDateAsMillis: b ? (b.ConsolidatedDate ? b.ConsolidatedDate.getTime() : null) : null,
                    consolidatedDeliveryDate: b ? (b.ConsolidatedDeliveryDate ? formatDate(b.ConsolidatedDeliveryDate, 'mediumDate', 'de-DE') : '') : '',
                    consolidatedDeliveryDateAsMillis: b ? (b.ConsolidatedDeliveryDate ? b.ConsolidatedDeliveryDate.getTime() : null) : null,
                    dueDate: b ? (b.DueDate ? formatDate(b.DueDate, 'mediumDate', 'de-DE') : '') : '',
                    dueDateAsMillis: b ? (b.DueDate ? b.DueDate.getTime() : null) : null,
                    exportDate: b ? (b.ExportDate ? formatDate(b.ExportDate, 'mediumDate', 'de-DE') : '') : '',
                    exportDateAsMillis: b ? (b.ExportDate ? b.ExportDate.getTime() : null) : null,
                    exportTimespan: exportTimespan,
                    lastPaymentDate: lastPayment ? formatDate(lastPayment.Zahlungsdatum, 'dd.MM.yyyy', 'de-DE') : '',
                    lastPaymentDateAsMillis: lastPayment ? lastPayment.Zahlungsdatum.getTime() : null,
                    lastPaymentNote: lastPayment ? lastPayment.ZahlungseingangZusatz : '',
                    lastPaymentGross: lastPayment ? formatCurrency(lastPayment.ZahlungseingangBetragBrutto / 100, 'de-DE', '€', 'EUR') : '',
                    lastPaymentNet: lastPayment ? formatCurrency(lastPayment.ZahlungseingangBetragNetto / 100, 'de-DE', '€', 'EUR') : '',
                    payedAmountGross: b.PayedAmountGross ? formatCurrency(b.PayedAmountGross / 100, 'de-DE', '€', 'EUR') : '',
                    amountGross: b.AmountGross ? formatCurrency(b.AmountGross / 100, 'de-DE', '€', 'EUR') : '',
                    discountValue: b.DiscountValue ? formatCurrency(b.DiscountValue, 'de-DE', '€', 'EUR') : '',
                    discountValueGross: b.DiscountValueGross ? formatCurrency(MathRound(b.DiscountValueGross / 100, true), 'de-DE', '€', 'EUR') : '',
                    ledgerImportDocType: b.LedgerImportId && LedgerImportDocumentTypeNames.get(ledgerImportDict[b.LedgerImportId]?.DocumentType),
                    ledgerImportDocTypeId: b.LedgerImportId && ledgerImportDict[b.LedgerImportId]?.DocumentType,
                };
            });
        }),
        shareReplay({refCount: true, bufferSize: 1}),
    );

    readonly length$ = new BehaviorSubject<number>(0);
    readonly currentFilteredData$ = new BehaviorSubject<BusinessVolumeTableItem[]>([]);
    constructor(private BVS$: Observable<BusinessVolumeEntity[]>) {
        super();
    }

    /**
     * Connect this data source to the table. The table will only update when
     * the returned stream emits new items.
     * @returns A stream of the items to be rendered.
     */
    connect(): Observable<BusinessVolumeTableItem[]> {
        if (this.paginator && this.sort && this.filter$) {
            // Combine everything that affects the rendered data into one update
            // stream for the data-table to consume.
            this.loading$.next(true);
            return this.data$.pipe(
                tap(() => this.loading$.next(false)),
                switchMap(data => this.sort.sortChange.pipe(
                    startWith(null),
                    map(() => this.getSortedData([...data])),
                )),
                switchMap(data => this.filter$.pipe(
                    // startWith(null),
                    map((filter) => this.getFilteredData(data, filter)),
                )),
                switchMap(data => this.searchQuery$.pipe(
                    debounceTime(SearchQueriesDebounceTime),
                    startWith(''),
                    map(() => (this.searchQuery$.value || '').trim().toLowerCase()),
                    distinctUntilChanged(),
                    map((search) => this.getSearchedData(data, search)),
                )),
                tap(data => {
                    this.currentFilteredData$.next(data);
                    this.length$.next(data.length);
                    this.paginator.length = data.length;
                    this.paginator.pageIndex = 0;
                }),
                switchMap(data => this.paginator.page.pipe(
                    startWith(null),
                    map(() => {
                        return this.getPagedData(data);
                    }),
                )),
            );
        } else {
            console.log('Please set the filter$, paginator and sort on the data source before connecting.', {
                searchQuery$: this.searchQuery$,
                filter$: this.filter$,
                paginator: this.paginator,
                sort: this.sort,
            });
            throw Error('Please set the filter$, paginator and sort on the data source before connecting.');
        }
    }

    /**
     *  Called when the table is being destroyed. Use this function, to clean up
     * any open connections or free any held resources that were set up during connect.
     */
    disconnect(): void {
    }

    /**
     * Paginate the data (client-side). If you're using server-side pagination,
     * this would be replaced by requesting the appropriate data from the server.
     */
    private getPagedData(data: BusinessVolumeTableItem[]): BusinessVolumeTableItem[] {
        if (this.paginator) {
            const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
            return data.slice().splice(startIndex, this.paginator.pageSize);
        } else {
            return data;
        }
    }

    /**
     * Sort the data (client-side). If you're using server-side sorting,
     * this would be replaced by requesting the appropriate data from the server.
     */
    private getSortedData(data: BusinessVolumeTableItem[]): BusinessVolumeTableItem[] {
        if (!this.sort || !this.sort.active || this.sort.direction === '') {
            return data;
        }

        return data.sort((a, b) => {
            const isAsc = this.sort?.direction === 'asc';
            return compare(sortingDataAccessor(a, this.sort?.active), sortingDataAccessor(b, this.sort?.active), isAsc);
        });
    }

    private getFilteredData(data: BusinessVolumeTableItem[], filterValues: BvFilterValues): BusinessVolumeTableItem[] {
        const commissionFilter = [...(filterValues[FilterTypes.Commissions] || []).map(c => c.id), ...(filterValues[FilterTypes.SubCommissions] || []).map(c => c.id)];
        let res = data.filter(
            (d) =>
                (!filterValues[FilterTypes.Type] || filterValues[FilterTypes.Type] === d.bvEntity.BVType) &&
                (!filterValues[FilterTypes.LedgerImportDocTypes]?.length || filterValues[FilterTypes.LedgerImportDocTypes].some((f) => d.ledgerImportDocTypeId === f.id)) &&
                (!filterValues[FilterTypes.Status]?.length || filterValues[FilterTypes.Status].some((s) => s.id === d.bvEntity.Status)) &&
                (!filterValues[FilterTypes.CustomNames]?.length || filterValues[FilterTypes.CustomNames].some((s) => d.bvEntity.BVCustomNamesIds?.includes(+s.id))) &&
                (isNotNullOrUndefined(filterValues[FilterTypes.Export]) ? (JSON.parse(filterValues[FilterTypes.Export]) ? d.bvEntity.ExportDate : !d.bvEntity.ExportDate) : d) &&
                (isNotNullOrUndefined(filterValues[FilterTypes.Checked]) ? (JSON.parse(filterValues[FilterTypes.Checked]) ? d.checked : !d.checked) : d) &&
                (!commissionFilter.length || (d.bvEntity.CommissionId && commissionFilter.some((id) => id === d.bvEntity.CommissionId))) &&
                // (!filterValues[FilterTypes.Customers] !== null || (d.bvEntity.CustomerId && filterValues[FilterTypes.Customers] === d.bvEntity.CustomerId)) &&
                (!filterValues[FilterTypes.ContraChartOfAccounts]?.length || (d.bvEntity.SachkontoCOAId && filterValues[FilterTypes.ContraChartOfAccounts].some((f) => f.id === d.bvEntity.SachkontoCOAId))) &&
                (!filterValues[FilterTypes.Date]?.from || (filterValues[FilterTypes.Date].from as Moment).isSameOrBefore(d.bvEntity.CreatedAt, 'day')) &&
                (!filterValues[FilterTypes.Date]?.to || (filterValues[FilterTypes.Date].to as Moment).isSameOrAfter(d.bvEntity.CreatedAt, 'day')) &&
                (!filterValues[FilterTypes.ConsolidatedDate]?.from || (filterValues[FilterTypes.ConsolidatedDate].from as Moment).isSameOrBefore(d.bvEntity.ConsolidatedDate, 'day')) &&
                (!filterValues[FilterTypes.ConsolidatedDate]?.to || (filterValues[FilterTypes.ConsolidatedDate].to as Moment).isSameOrAfter(d.bvEntity.ConsolidatedDate, 'day')) &&
                (!filterValues[FilterTypes.DueDate]?.from || (filterValues[FilterTypes.DueDate].from as Moment).isSameOrBefore(d.bvEntity.DueDate, 'day')) &&
                (!filterValues[FilterTypes.DueDate]?.to || (filterValues[FilterTypes.DueDate].to as Moment).isSameOrAfter(d.bvEntity.DueDate, 'day')),
        );
        if (filterValues[FilterTypes.Customers] !== null) {
            res = res.filter((d) => d.bvEntity.CustomerId && filterValues[FilterTypes.Customers] === d.bvEntity.CustomerId);
        }
        return res;
    }

    private getSearchedData(data: BusinessVolumeTableItem[], searchQuery: string): BusinessVolumeTableItem[] {
        return searchQuery ? data.filter((item) => Object.values(item).some((value) => typeof value === 'string' && value.trim().toLowerCase().includes(searchQuery))) : data;
    }
}

const sortingDataAccessor = (object: BusinessVolumeTableItem, key: string): any | number => {
    switch (key as keyof BusinessVolumeTableItem) {
        case 'dueDate':
            return object.dueDateAsMillis;
        case 'createdAt':
            return object.createdAtAsMillis;
        case 'consolidatedDate':
            return object.consolidatedDateAsMillis;
        case 'consolidatedDeliveryDate':
            return object.consolidatedDeliveryDateAsMillis;
        case 'lastPaymentDate':
            return object.lastPaymentDateAsMillis;
        case 'exportDate':
            return object.exportDateAsMillis;
        case 'payedAmount':
            return object.payedAmountNumber;
        case 'amount':
            return object.amountNumber;
        default:
            return object[key];
    }
}

/** Simple sort comparator for example ID/Name columns (for client-side sorting). */
function compare(a: string | number, b: string | number, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
